import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Y, T, CREDIT, LABEL_FONT } from "../theme";
import { api } from "../firebase";
import { ModelIcon, CreditIcon } from "./shared";
import PageLoader from "./PageLoader";
import { AuthImage, AuthVideo } from "./GenerateView";

// Status pill - mirrors the queue table's color scheme.
const STATUS_COLOR = { queued: T.text3, processing: Y, ready: T.success, failed: T.danger };

// Render a Firestore-serialized timestamp (ISO string) as both absolute
// local time and a relative "Xs/m/h ago" hint. Returns "—" when null.
function fmtTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  const diff = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  let rel;
  if (diff < 60)        rel = `${diff}s ago`;
  else if (diff < 3600) rel = `${Math.floor(diff/60)}m ago`;
  else if (diff < 86400)rel = `${Math.floor(diff/3600)}h ago`;
  else                  rel = `${Math.floor(diff/86400)}d ago`;
  return `${d.toLocaleString()} · ${rel}`;
}

// Compact key-value row used inside each section.
function Field({ label, value, mono, danger }) {
  if (value === null || value === undefined || value === "") value = "—";
  return (
    <div style={{display:"grid",gridTemplateColumns:"160px 1fr",gap:12,padding:"5px 0",borderBottom:"1px solid rgba(255,255,255,0.04)",alignItems:"baseline"}}>
      <div style={{color:T.text3,fontSize:11,fontWeight:600,letterSpacing:0.3,textTransform:"uppercase"}}>{label}</div>
      <div style={{color:danger?T.danger:T.text2,fontSize:12,fontFamily:mono?"monospace":"inherit",wordBreak:"break-word",whiteSpace:"pre-wrap"}}>
        {value}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{background:"rgba(255,255,255,0.03)",borderRadius:12,padding:"16px 20px",border:"1px solid rgba(255,255,255,0.06)"}}>
      <div style={{fontFamily:LABEL_FONT,fontSize:11,fontWeight:700,letterSpacing:0.8,textTransform:"uppercase",color:T.title,marginBottom:10}}>{title}</div>
      <div>{children}</div>
    </div>
  );
}

export default function AdminJobDetailView({ jobId }) {
  const navigate = useNavigate();
  const [job, setJob]   = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovering, setRecovering] = useState(false);
  const [recoverResult, setRecoverResult] = useState(null);

  const reload = async () => {
    setError(null);
    try {
      const j = await api.getAdminJob(jobId);
      setJob(j);
    } catch (e) {
      setError(e.message || "Failed to load job");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const j = await api.getAdminJob(jobId);
        if (!cancelled) setJob(j);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load job");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [jobId]);

  const back = () => navigate("/admin/job-queue");

  const handleRecoverOutputs = async () => {
    setRecovering(true);
    setRecoverResult(null);
    try {
      const r = await api.recoverJobOutputs(jobId);
      setRecoverResult(r);
      if (r.recovered) await reload();
    } catch (e) {
      setRecoverResult({ recovered: false, error: e.message });
    } finally {
      setRecovering(false);
    }
  };

  if (loading) return (
    <div style={{display:"flex",flexDirection:"column",gap:24}}>
      <PageLoader ready={false}/>
    </div>
  );

  if (error || !job) return (
    <div style={{display:"flex",flexDirection:"column",gap:16}}>
      <button onClick={back} style={{alignSelf:"flex-start",padding:"6px 12px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",color:T.text2,fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
        Back to Job Queue
      </button>
      <div style={{padding:"16px 20px",borderRadius:10,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,color:T.dangerText,fontSize:12}}>
        {error || "Job not found"}
      </div>
    </div>
  );

  const isImage = job.outputType === "image";
  const credits = job.cost != null ? Math.round(Number(job.cost) / 0.002) : null;
  const refs = job.referenceImageGcsPaths || [];
  const outs = job.imageOutputGcsPaths || [];

  return (
    <div style={{display:"flex",flexDirection:"column",gap:18}}>
      {/* Header: back link + ID + status */}
      <div style={{display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <button onClick={back} title="Back to Job Queue"
          style={{padding:"6px 12px",borderRadius:8,background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",color:T.text2,fontSize:11,fontWeight:600,cursor:"pointer",display:"flex",alignItems:"center",gap:6}}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
          Back
        </button>
        <div style={{fontSize:16,fontWeight:800,color:T.title}}>Job</div>
        <div style={{fontFamily:"monospace",fontSize:13,color:T.text2}}>{job.id}</div>
        <div style={{padding:"3px 10px",borderRadius:999,background:`${STATUS_COLOR[job.status] || T.text3}22`,color:STATUS_COLOR[job.status] || T.text3,fontSize:10,fontWeight:800,letterSpacing:0.6,textTransform:"uppercase"}}>
          {job.queuePending ? "queued (waiting)" : job.status}
        </div>
        {job.deleted && (
          <div style={{padding:"3px 10px",borderRadius:999,background:"rgba(255,45,111,0.1)",color:T.danger,fontSize:10,fontWeight:800,letterSpacing:0.6,textTransform:"uppercase"}}>
            deleted
          </div>
        )}
      </div>

      {/* Output preview - the most useful thing on the page, so it's first */}
      <Section title="Output">
        {isImage && job.status === "ready" && outs.length > 0 ? (
          <div style={{display:"flex",gap:10,flexWrap:"wrap"}}>
            {outs.map((_, idx) => (
              <AuthImage key={idx} src={api.getOutputImageUrl(job.id, idx, { thumb: true })}
                style={{maxWidth:280,maxHeight:280,borderRadius:6,background:"#000",cursor:"default"}}/>
            ))}
          </div>
        ) : !isImage && job.status === "ready" && job.gcsPath ? (
          <AuthVideo src={api.getVideoUrl(job.id)}
            style={{width:"100%",maxWidth:560,borderRadius:6,background:"#000"}}/>
        ) : !isImage && job.posterGcsPath ? (
          <div>
            <AuthImage src={api.getPosterUrl(job.id, { thumb: true })} alt="poster"
              style={{maxWidth:280,maxHeight:280,borderRadius:6,background:"#000"}}/>
            <div style={{color:T.text3,fontSize:10,marginTop:4}}>Poster (first frame) - video not available</div>
          </div>
        ) : (
          <div style={{color:T.text3,fontSize:12}}>No output yet ({job.status}).</div>
        )}
      </Section>

      {/* Error - only when set, but always shown prominently when present */}
      {job.error && (
        <Section title="Error">
          <div style={{padding:"10px 12px",borderRadius:8,background:T.dangerBg,border:`1px solid ${T.dangerBorder}`,color:T.dangerText,fontSize:12,fontFamily:"monospace",whiteSpace:"pre-wrap",wordBreak:"break-word",lineHeight:1.5}}>
            {job.error}
          </div>
          {/* Failed image-gen jobs may still have generated bytes sitting in
              GCS at outputs/<uid>/<jobId>/ - the upstream API call ran but
              the job doc never got updated. Offer to scan + restore. */}
          {job.outputType === "image" && job.status !== "ready" && (
            <div style={{marginTop:10,display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <button onClick={handleRecoverOutputs} disabled={recovering}
                style={{padding:"6px 14px",borderRadius:8,background:"rgba(91,191,184,0.1)",border:"1px solid rgba(91,191,184,0.4)",color:"#5BBFB8",fontSize:11,fontWeight:700,cursor:recovering?"wait":"pointer",display:"flex",alignItems:"center",gap:6}}>
                {recovering ? "Checking GCS…" : "Check GCS for outputs"}
              </button>
              {recoverResult && (
                <span style={{fontSize:11,color:recoverResult.recovered?T.success:(recoverResult.error?T.danger:T.text3)}}>
                  {recoverResult.recovered
                    ? `Recovered ${recoverResult.count} output${recoverResult.count===1?"":"s"} - job set to ready.`
                    : (recoverResult.error || recoverResult.message || "No outputs found.")}
                </span>
              )}
            </div>
          )}
        </Section>
      )}

      {/* Source frames + references */}
      {(job.imageGcsPath || job.endImageGcsPath || refs.length > 0) && (
        <Section title="Inputs">
          <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
            {job.imageGcsPath && (
              <div>
                <AuthImage src={api.getImageUrl(job.id, { thumb: true })} alt="start frame"
                  style={{width:140,height:140,objectFit:"cover",borderRadius:6,background:"#000"}}/>
                <div style={{color:T.text3,fontSize:10,marginTop:4,textAlign:"center"}}>Start frame</div>
              </div>
            )}
            {job.endImageGcsPath && (
              <div>
                <AuthImage src={api.getEndImageUrl(job.id, { thumb: true })} alt="end frame"
                  style={{width:140,height:140,objectFit:"cover",borderRadius:6,background:"#000",border:`1px solid ${Y}30`}}/>
                <div style={{color:T.text3,fontSize:10,marginTop:4,textAlign:"center"}}>End frame</div>
              </div>
            )}
            {refs.length > 0 && (
              <div>
                <div style={{display:"flex",gap:6,flexWrap:"wrap",maxWidth:300}}>
                  {refs.map((_, idx) => (
                    <AuthImage key={idx} src={api.getReferenceImageUrl(job.id, idx, { thumb: true })} alt={`ref ${idx+1}`}
                      style={{width:66,height:66,objectFit:"cover",borderRadius:6,background:"#000"}}/>
                  ))}
                </div>
                <div style={{color:T.text3,fontSize:10,marginTop:4}}>{refs.length} reference{refs.length===1?"":"s"}</div>
              </div>
            )}
          </div>
        </Section>
      )}

      {/* Identity */}
      <Section title="Identity">
        <Field label="User" value={`${job.email || "—"}${job.uid ? `  (${job.uid})` : ""}`} mono/>
        <Field label="Platform" value={job.platform || "—"}/>
        <Field label="Model" value={(
          <span style={{display:"inline-flex",alignItems:"center",gap:6}}>
            {job.model && <ModelIcon model={job.model} s={14}/>}
            <span style={{fontFamily:"monospace"}}>{job.model || "—"}</span>
          </span>
        )}/>
        <Field label="Output type" value={job.outputType || "—"}/>
        <Field label="Input mode" value={job.inputMode || "—"}/>
      </Section>

      {/* Generation parameters - layout depends on outputType */}
      <Section title="Parameters">
        <Field label="Prompt" value={job.prompt}/>
        <Field label="Negative prompt" value={job.negativePrompt}/>
        <Field label="Aspect ratio" value={job.aspectRatio}/>
        {!isImage && <Field label="Duration" value={job.duration ? `${job.duration}s` : "—"}/>}
        {!isImage && <Field label="Audio" value={job.audio === true ? "yes" : (job.audio === false ? "no" : "—")}/>}
        {!isImage && <Field label="CFG scale" value={job.cfgScale}/>}
        {isImage && <Field label="Resolution" value={job.imageResolution}/>}
        {isImage && <Field label="Output count (n)" value={job.n}/>}
        {(job.cropPosition !== undefined || job.endCropPosition !== undefined) && (
          <Field label="Crop position" value={`start: ${job.cropPosition ?? "—"} · end: ${job.endCropPosition ?? "—"}`} mono/>
        )}
      </Section>

      {/* Cost / billing */}
      <Section title="Cost">
        <Field label="Cost (USD)" value={job.cost != null ? `$${Number(job.cost).toFixed(4)}` : "—"} mono/>
        {job.geminiUsage && (
          <>
            <Field label="Gemini prompt tokens"     value={job.geminiUsage.promptTokenCount}     mono/>
            <Field label="Gemini candidate tokens"  value={job.geminiUsage.candidatesTokenCount} mono/>
            <Field label="Gemini total tokens"      value={job.geminiUsage.totalTokenCount}      mono/>
            <Field label="Gemini API calls"         value={job.geminiUsage.calls}                mono/>
          </>
        )}
        <Field label="Credits" value={credits != null ? (
          <span style={{display:"inline-flex",alignItems:"center",gap:4}}>
            <CreditIcon size={11} color={CREDIT}/>{credits.toLocaleString()}
          </span>
        ) : "—"}/>
        <Field label="Kling units" value={job.units} mono/>
        <Field label="Used studio keys" value={job.usedStudioKeys === true ? "yes" : (job.usedStudioKeys === false ? "no" : "—")}/>
        <Field label="Queue pending" value={job.queuePending === true ? "yes" : "no"}/>
      </Section>

      {/* Upstream task IDs + endpoints - useful for debugging */}
      <Section title="Upstream">
        <Field label="Kling task ID" value={job.klingTaskId} mono/>
        <Field label="Grok request ID" value={job.grokRequestId} mono/>
        <Field label="Google operation" value={job.googleOperationName} mono/>
        <Field label="Video endpoint" value={job.videoEndpoint}/>
        <Field label="Image endpoint" value={job.imageEndpoint}/>
        <Field label="Omni" value={job.isOmni === true ? "yes" : (job.isOmni === false ? "no" : "—")}/>
      </Section>

      {/* Storage paths (admins might need them for direct GCS lookup) */}
      <Section title="Storage paths">
        <Field label="Image (start)" value={job.imageGcsPath} mono/>
        <Field label="End image" value={job.endImageGcsPath} mono/>
        <Field label="References" value={refs.length ? refs.join("\n") : "—"} mono/>
        <Field label="Output images" value={outs.length ? outs.join("\n") : "—"} mono/>
        <Field label="Video" value={job.gcsPath} mono/>
        <Field label="Poster" value={job.posterGcsPath} mono/>
      </Section>

      {/* Timestamps */}
      <Section title="Timestamps">
        <Field label="Created" value={fmtTime(job.createdAt)}/>
        <Field label="Processing started" value={fmtTime(job.processingStartedAt)}/>
        <Field label="Last poll" value={fmtTime(job.lastPollAt)}/>
        <Field label="Updated" value={fmtTime(job.updatedAt)}/>
        <Field label="Completed" value={fmtTime(job.completedAt)}/>
        {job.stuckNotifiedAt && <Field label="Stuck notified" value={fmtTime(job.stuckNotifiedAt)} danger/>}
        {job.deleted && <Field label="Deleted" value={fmtTime(job.deletedAt)} danger/>}
      </Section>
    </div>
  );
}
